import React from 'react';
import { getAllLangs, langType } from '../../../../src/lang';
import { SystemShared } from '../../../../shared/system';
import { UserDataWeb, UserOrdersWeb } from '../../../../shared/userdataweb';
import { CURRENCY_NAME } from '../../../../shared/currency';
import { PaymentType } from '../../../../shared/payments';
import { NewsItem } from '../../../../shared/news';
import { ProductID } from '../../../../shared/products';
import { WebStorage } from '../../../storage';
import { loadAdminNews } from './admin.api';

export const useAdminData = (loaded: boolean, hasAdmin: boolean) => {
    const [pausedAlert, setPausedAlert] = React.useState(false);
    const [usersList, setUsersList] = React.useState<UserDataWeb[]>([]);
    const [ordersList, setOrdersList] = React.useState<UserOrdersWeb[]>([]);
    const [disk, setDisk] = React.useState(0);

    const [paymentsList, setPaymentsList] = React.useState<
        {
            sum: number;
            currency: CURRENCY_NAME;
            timestamp: number;
            type: PaymentType;
            user: number;
        }[]
    >([]);

    const [promoList, setPromoList] = React.useState<
        {
            id: number;
            code: string;
            remain: number;
            used: number;
            amount: number;
            currency: CURRENCY_NAME;
            created: number;
        }[]
    >([]);

    const [promoListUsed, setPromoListUsed] = React.useState<
        {
            id: number;
            code: string;
            who: number;
            time: number;
        }[]
    >([]);

    const [changeLogs, setChangeLogs] = React.useState<{ id: string; date: string }[]>([]);

    const [news, setNews] = React.useState<NewsItem[]>([]);
    const [extensions, setExtensions] = React.useState<any[]>([]);

    const loadNews = React.useCallback(() => {
        loadAdminNews().then((res) => {
            if (res) {
                setNews(res);
            }
        });
    }, []);

    const loadExtensions = React.useCallback(() => {
        WebStorage.ApiRequest('/api/admin/extensions/list').then((res) => {
            if (res.status) {
                setExtensions(res.data);
            }
        });
    }, []);

    const loadPaymentsList = React.useCallback(() => {
        WebStorage.ApiRequest('/api/getHistoryPayAdmin').then((data) => {
            if (data.status) {
                setPaymentsList((old) => {
                    if (JSON.stringify(old) === JSON.stringify(data.data)) {
                        return old;
                    }

                    return data.data;
                });
            }
        });
    }, []);

    const loadUsersList = React.useCallback(() => {
        WebStorage.ApiRequest('/api/admin/users').then((data) => {
            if (data.status) {
                setUsersList((old) => {
                    if (JSON.stringify(old) === JSON.stringify(data.data)) {
                        return old;
                    }

                    return data.data;
                });
            }
        });
    }, []);

    const loadOrdersList = React.useCallback(() => {
        WebStorage.ApiRequest('/api/admin/orders').then((data) => {
            if (data.status) {
                setOrdersList((old) => {
                    if (JSON.stringify(old) === JSON.stringify(data.data)) {
                        return old;
                    }

                    return data.data;
                });
            }
        });
    }, []);

    const loadPromoList = React.useCallback(() => {
        WebStorage.ApiRequest('/api/admin/promo').then((data) => {
            if (data.status) {
                setPromoList((old) => {
                    if (JSON.stringify(old) === JSON.stringify(data.data)) {
                        return old;
                    }

                    return data.data;
                });
            }
        });
    }, []);

    const loadPromoListUsed = React.useCallback(() => {
        WebStorage.ApiRequest('/api/admin/promoused').then((data) => {
            if (data.status) {
                setPromoListUsed((old) => {
                    if (JSON.stringify(old) === JSON.stringify(data.data)) {
                        return old;
                    }

                    return data.data;
                });
            }
        });
    }, []);

    const loadAlert = React.useCallback(() => {
        WebStorage.ApiRequest('/api/admin/loadAlert').then((data) => {
            if (data.status) {
                setPausedAlert((old) => {
                    if (JSON.stringify(old) === JSON.stringify(data.data)) {
                        return old;
                    }

                    return data.data;
                });
            }
        });
    }, []);

    const loadDisk = React.useCallback(() => {
        WebStorage.ApiRequest('/api/admin/disk').then((data) => {
            if (data.status) {
                setDisk(data.size);
            }
        });
    }, []);

    const loadChangelogs = React.useCallback(() => {
        WebStorage.ApiRequest('/api/admin/changelog/get').then((data) => {
            if (data.status) {
                setChangeLogs(data.files);
            }
        });
    }, []);

    React.useEffect(() => {
        if (!loaded || !hasAdmin) {
            return;
        }

        loadDisk();
        loadChangelogs();
        loadNews();
        loadExtensions();

        const intTick = () => {
            loadPaymentsList();
            loadUsersList();
            loadOrdersList();
            loadPromoList();
            loadPromoListUsed();
            loadAlert();
        };

        intTick();

        const int = setInterval(intTick, 30000);

        return () => {
            clearInterval(int);
        };
    }, [
        loaded,
        hasAdmin,
        loadDisk,
        loadChangelogs,
        loadNews,
        loadExtensions,
        loadPaymentsList,
        loadUsersList,
        loadOrdersList,
        loadPromoList,
        loadPromoListUsed,
        loadAlert,
    ]);

    const diskSizeGB = React.useMemo(() => disk / 1024 / 1024 / 1024, [disk]);

    return {
        pausedAlert,
        usersList,
        ordersList,
        disk,
        diskSizeGB,
        paymentsList,
        promoList,
        promoListUsed,
        changeLogs,
        news,
        setNews,
        extensions,
        loadNews,
        loadExtensions,
        loadPaymentsList,
        loadUsersList,
        loadOrdersList,
        loadPromoList,
        loadPromoListUsed,
        loadAlert,
        loadDisk,
        loadChangelogs,
    };
};

export const createEmptyNewsEdit = (): NewsItem => ({
    id: SystemShared.makeid(5),
    data: getAllLangs()
        .map((q) => ({
            lang: q,
            title: '',
            img: '',
            text: '',
        }))
        .reduce(
            (a, v) => ({
                ...a,
                [v.lang as langType]: {
                    title: v.title,
                    img: v.img,
                    text: v.text,
                },
            }),
            {},
        ) as any,
    timestamp: SystemShared.timestamp,
});

export type GiveRewardState = {
    product: ProductID;
    days: number;
    reason: number;
};
